import React, { useMemo } from 'react';
import useSetExport from '@components/export/useSetExport';
import { unzipWith } from 'lodash';
import { selectedAiAlertSelector } from '@store/trends/aiAlerts/selectors';
import { useSelector } from 'react-redux';
import { formatAlertSummary } from '@components/alert-panel/AIAlert/hooks/utils';
import { useDrilldownMeta } from '@src/hooks/common/useDrilldownMeta';
import { getMetricDisplayName } from '@src/utils/metrics';
import metricsStore from '@src/globalStore/metricsStore';

function useSetAIAlertExport() {
  const alert = useSelector(selectedAiAlertSelector);
  const { flowConfigMeta } = metricsStore.getState();
  const { flowId, frustrationSignalId } = useDrilldownMeta();

  // summary
  const info = useMemo(() => {
    if (alert) {
      return formatAlertSummary(alert, flowId, frustrationSignalId);
    }
    return {};
  }, [alert]);

  const {
    alertId,
    metricDisplayName,
    rootCause,
    timeEndedText,
    alertEndedText,
    issueStartTime,
    issueEndTime,
    peakValue,
    cumulativeImpactedUniques,
    impactedPercentage,
    metricName,
  } = info;

  const infoMetricDisplayName =
    getMetricDisplayName({
      metricKey: metricName,
      flowOverride: !!flowId,
      frustrationSignalOverride: !!frustrationSignalId,
    }) || metricDisplayName;

  const data = [['Metric', infoMetricDisplayName]];

  if (flowConfigMeta[flowId] && flowId) {
    data.push(['Flow Name', flowConfigMeta[flowId].name]);
  }

  if ('status' in info) {
    data.push(['Status', alertEndedText]);
  }
  if ('peakValue' in info) {
    data.push(['Peak Value', peakValue]);
  }
  data.push(
    ['AlertId', alertId],
    ['Time issue started', issueStartTime],
    [timeEndedText, issueEndTime],
  );
  if ('cumulativeImpactedUniques' in info) {
    data.push([
      'Cumulative Impacted Unique Devices',
      `Count: ${cumulativeImpactedUniques} | Percentage: ${impactedPercentage}%`,
    ]);
  }
  data.push(['Root Cause', rootCause]);

  // timeseries
  const { graphSeries } = alert;

  useSetExport({
    pageKey: 'trends',
    sectionKey: 'alerts-panel',
    widgetKey: 'ai-alert-summary',
    widgetInfo: { title: 'AI Alert Summary' },
    data,
    headers: [
      { title: '', key: 'field' },
      { title: '', key: 'value' },
    ],
  });

  const chartHeaders = [{ title: 'Timestamp', key: 'timestamp' }];
  const dataGroup: any = [];
  let flatData: any = [];

  graphSeries.forEach((series) => {
    // baseline and threshold data points are not matching with other series
    // skip as it's the existing export behaviour
    if (!['Threshold', 'Baseline'].includes(series.name)) {
      chartHeaders.push({ title: series.name, key: series.seriesType });
      dataGroup.push(series.data);
    }
  });

  // time + metrics
  try {
    flatData = unzipWith(dataGroup, (...group) => {
      return [group[0][0], ...group.map((g) => g[1])];
    });
  } catch (err) {
    // pass
  }

  useSetExport({
    pageKey: 'trends',
    sectionKey: 'alerts-panel',
    widgetKey: 'ai-alert-series',
    widgetInfo: { title: 'AI Alert Timeseries' },
    data: flatData,
    headers: chartHeaders,
  });
}

export default useSetAIAlertExport;
